import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useGetBontonListingItemDetailsMutation, useGetBontonPicklistListMutation } from "../../../features/supplyBeam/supplyBeamData/bontonListingAPI";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import ListingItemDetailsModal from "./ListingItemDetailsModal";

const PicklistList = () => {
    const [
        fetchPicklistList,
        {
            data: picklistListData,
            error: picklistListError,
            isLoading: picklistListIsLoading,
        },
    ] = useGetBontonPicklistListMutation();
    const [
        fetchItemDetails,
        {
            data: itemDetailsData,
            error: itemDetailsError,
            isLoading: itemDetailsIsLoading,
        },
    ] = useGetBontonListingItemDetailsMutation();

    const [myList, setMyList] = useState([]);
    const [showItemDetailsModal, setShowItemDetailsModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [totalRows, setTotalRows] = useState(0);
    const [limit, setLimit] = useState(10);
    const [offset, setOffset] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [paginationResetToggle, setPaginationResetToggle] = useState(false);

    const initialRequest = {
        page: 1,
        limit: 10,
        order_no: "",
        serial_number: "",
        status: "",
        start_date: "",
        end_date: "",
    };

    const [request, setRequest] = useState(initialRequest);

    const clearFilters = () => {
        setRequest(initialRequest);
        setCurrentPage(1);
        setOffset(0);
    };

    useEffect(() => {
        fetchPicklistListFunction();
    }, [request]);

    const fetchPicklistListFunction = () => {
        fetchPicklistList({
            token: sessionStorage.getItem("access_token"),
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
            data: request,
        });
    };

    useEffect(() => {
        if (picklistListData) {
            setMyList(picklistListData?.data?.list || []);
            setTotalRows(picklistListData?.data?.pagination?.total || 0);
        }
        if (picklistListError) {
            toast.dismiss();
            toast.error("Something went wrong", { id: "picklistListError" });
        }
    }, [picklistListData, picklistListError]);

    useEffect(() => {
        if (itemDetailsError) {
            toast.dismiss();
            toast.error("Unable to fetch item details", { id: "itemDetailsError" });
            setShowItemDetailsModal(false);
        }
    }, [itemDetailsError]);

    const handleViewDetails = (row) => {
        setSelectedRow(row);
        setShowItemDetailsModal(true);
        fetchItemDetails({
            token: sessionStorage.getItem("access_token"),
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
            data: {
                type: "picklist",
                id: row.id,
                order_no: row.order_no,
                serial_number: row.serial_number,
            },
        });
    };

    const handlePageChange = (pageNumber) => {
        const newOffset = (pageNumber - 1) * limit;
        setOffset(newOffset);
        setRequest((prev) => ({ ...prev, page: pageNumber }));
        setCurrentPage(pageNumber);
    };

    const handlePerRowsChange = (newPerPage) => {
        setRequest((prev) => ({
            ...prev,
            limit: newPerPage,
            page: 1,
        }));
        setLimit(newPerPage);
        setOffset(0);
        setCurrentPage(1);
        setPaginationResetToggle((prev) => !prev);
    };

    const getStatusBadge = (status) => {
        switch (Number(status)) {
            case 0:
                return (
                    <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                        Pending
                    </span>
                );
            case 1:
                return (
                    <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                        Active
                    </span>
                );
            case 2:
                return (
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                        Checked Out
                    </span>
                );
            default:
                return (
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                        {status}
                    </span>
                );
        }
    };

    const columns = [
        {
            name: "SNo",
            cell: (row, idx) => offset + Number(idx + 1),
            sortable: true,
            width: "80px",
        },
        {
            name: "Order No",
            cell: (row) => row.order_no,
            sortable: true,
            width: "200px",
        },
        {
            name: "Serial Number",
            cell: (row) => row.serial_number,
            sortable: true,
            width: "150px",
        },
        {
            name: "Status",
            cell: (row) => getStatusBadge(row.status),
            sortable: true,
            width: "130px",
        },
        {
            name: "Created By",
            cell: (row) => row.created_by,
            sortable: true,
            width: "150px",
        },
        {
            name: "Created Date",
            cell: (row) => row.created_date ? new Date(row.created_date).toLocaleString() : "-",
            sortable: true,
            width: "200px",
        },
        {
            name: "Action",
            cell: (row) => (
                <button
                    className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700"
                    onClick={() => handleViewDetails(row)}
                >
                    View
                </button>
            ),
            width: "110px",
        },
    ];

    return (
        <>
            <div className="flex w-full bg-gray-200 h-full justify-center">
                <div className="w-full">
                    <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
                        Picklist List
                        <LinksComponent />
                    </div>

                    <div className="p-3">
                        <div className="w-full bg-white shadow rounded-lg">
                            <div>
                                <DataTableComponent
                                    keyField={(row) => row.id}
                                    columns={columns}
                                    data={myList}
                                    loading={picklistListIsLoading}
                                    totalRows={totalRows}
                                    handlePerRowsChange={handlePerRowsChange}
                                    handlePageChange={handlePageChange}
                                    paginationDefaultPage={currentPage}
                                    paginationResetDefaultPage={paginationResetToggle}
                                >
                                    <div className="bg-gray-50 border-b border-gray-200 p-4">
                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
                                            <div className="flex flex-col">
                                                <label className="text-xs font-semibold text-gray-600 mb-1">Order No</label>
                                                <input
                                                    type="text"
                                                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                                                    placeholder="Search Order No..."
                                                    value={request.order_no}
                                                    onChange={(e) =>
                                                        setRequest({ ...request, order_no: e.target.value, page: 1 })
                                                    }
                                                />
                                            </div>
                                            <div className="flex flex-col">
                                                <label className="text-xs font-semibold text-gray-600 mb-1">Serial Number</label>
                                                <input
                                                    type="text"
                                                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                                                    placeholder="Search Serial No..."
                                                    value={request.serial_number}
                                                    onChange={(e) =>
                                                        setRequest({ ...request, serial_number: e.target.value, page: 1 })
                                                    }
                                                />
                                            </div>
                                            <div className="flex flex-col">
                                                <label className="text-xs font-semibold text-gray-600 mb-1">Status</label>
                                                <select
                                                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                                                    value={request.status}
                                                    onChange={(e) =>
                                                        setRequest({ ...request, status: e.target.value, page: 1 })
                                                    }
                                                >
                                                    <option value="">All</option>
                                                    <option value="0">Pending</option>
                                                    <option value="1">Active</option>
                                                    <option value="2">Checked Out</option>
                                                </select>
                                            </div>
                                            <div className="flex flex-col">
                                                <label className="text-xs font-semibold text-gray-600 mb-1">From Date</label>
                                                <input
                                                    type="date"
                                                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                                                    value={request.start_date}
                                                    onChange={(e) =>
                                                        setRequest({ ...request, start_date: e.target.value, page: 1 })
                                                    }
                                                />
                                            </div>
                                            <div className="flex flex-col">
                                                <label className="text-xs font-semibold text-gray-600 mb-1">To Date</label>
                                                <input
                                                    type="date"
                                                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                                                    value={request.end_date}
                                                    onChange={(e) =>
                                                        setRequest({ ...request, end_date: e.target.value, page: 1 })
                                                    }
                                                />
                                            </div>
                                            <div className="flex items-end gap-2">
                                                <button
                                                    className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-blue-700 transition shadow-sm"
                                                    onClick={fetchPicklistListFunction}
                                                >
                                                    Search
                                                </button>
                                                <button
                                                    className="rounded-md bg-gray-200 px-4 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-300 transition"
                                                    onClick={clearFilters}
                                                >
                                                    Clear
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </DataTableComponent>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showItemDetailsModal && (
                <ListingItemDetailsModal
                    show={showItemDetailsModal}
                    setShow={setShowItemDetailsModal}
                    title={`Picklist Items - ${selectedRow?.serial_number || selectedRow?.order_no || ""}`}
                    data={itemDetailsData}
                    loading={itemDetailsIsLoading}
                />
            )}
        </>
    );
};

export default PicklistList;
